import { DownOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, Card, Dropdown, Input, message, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TableRowSelection } from 'antd/es/table/interface'
import {
  AccountVestingSearchParams,
  AccountVestingType,
  ApproveAccountVestingParams,
} from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import {
  ACCOUNT_VESTING_STATUS_MAP,
  ACCOUNT_VESTING_STATUS_OPTIONS,
  AccountVestingStatusEnum,
  ALLOCATION_TITLE,
} from 'constants/vesting'
import { useAccountVestingContext } from 'context/account-vesting-context-provider'
import useSearchParams from 'hooks/use-search-params'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logInfo } from 'utils/logger'

interface IAccountVestingContentProps {
  title?: React.ReactNode
}

const AccountVestingContent = ({ title }: IAccountVestingContentProps) => {
  const { type } = useAccountVestingContext()

  const { searchParams, initPagination, onChangePagination, onSearch } =
    useSearchParams<AccountVestingSearchParams>({ type: type })

  const [data, setData] = useState<AccountVestingType[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const additionalItems = [BREADCRUMBS.VESTING_DATA]

  const isDisabledStatus = (status?: number | null) =>
    status === AccountVestingStatusEnum.LESS || status === AccountVestingStatusEnum.EQUAL

  const isFilterDisabledStatus = isDisabledStatus(searchParams.status)

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const res = await AppAdmin.getListAccountVesting(searchParams)

      if (res?.data.success) {
        const { results, totalResults } = res.data?.data || {}
        setTotal(totalResults || 0)
        setData(results || [])
      }
    } catch (error) {
      setTotal(0)
      setData([])
      logInfo('Error: ', error)
    } finally {
      setLoading(false)
    }
  }, [searchParams])

  useEffect(() => {
    getData()
  }, [getData])

  const handleSyncData = async () => {
    if (!type) {
      message.error('Vesting type is required')
      return
    }
    try {
      onOpenModalProcessing('Syncing data...')
      const res = await AppAdmin.syncAccountVesting({ type })
      if (res?.data?.success) {
        message.success('Sync data successfully')
        getData()
      } else {
        message.error(res?.data?.message || 'Sync data failed')
      }
    } catch (error: any) {
      message.error(error?.message || 'Sync data failed')
      logInfo('Error: ', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  const handleApproveAll = async () => {
    if (!type) {
      message.error('Vesting type is required')
      return
    }
    if (isFilterDisabledStatus) {
      message.error('Cannot approve items with status Less or Equal')
      return
    }
    try {
      onOpenModalProcessing('Approving all items...')
      const params: ApproveAccountVestingParams = { type }
      const res = await AppAdmin.approveAccountVesting(params)

      if (res?.data?.success) {
        message.success('Approve all successfully')
        getData()
      } else {
        message.error(res?.data?.message || 'Approve all failed')
      }
    } catch (error: any) {
      message.error(error?.message || 'Approve all failed')
      logInfo('Error: ', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  const handleApproveSelected = async () => {
    if (!type) {
      message.error('Vesting type is required')
      return
    }
    const hasSelectedStatus = searchParams.status !== undefined && searchParams.status !== null
    if (!hasSelectedStatus && selectedRowKeys.length === 0) {
      message.warning('Please select items to approve')
      return
    }
    if (isFilterDisabledStatus) {
      message.error('Cannot approve items with status Less or Equal')
      return
    }
    try {
      onOpenModalProcessing('Approving selected items...')
      const params: ApproveAccountVestingParams = { type }
      if (hasSelectedStatus) {
        params.status = searchParams.status
      }
      if (selectedRowKeys.length > 0) {
        params.ids = selectedRowKeys as string[]
      }
      const res = await AppAdmin.approveAccountVesting(params)

      if (res?.data?.success) {
        message.success('Approve selected successfully')
        setSelectedRowKeys([])
        getData()
      } else {
        message.error(res?.data?.message || 'Approve selected failed')
      }
    } catch (error: any) {
      message.error(error?.message || 'Approve selected failed')
      logInfo('Error: ', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  const approveMenuItems = [
    {
      key: 'approve-all',
      label: 'Approve All',
      onClick: handleApproveAll,
      disabled: isFilterDisabledStatus,
    },
    {
      key: 'approve-selected',
      label: `Approve Selected (${selectedRowKeys.length})`,
      onClick: handleApproveSelected,
      disabled:
        isFilterDisabledStatus ||
        ((searchParams.status === undefined || searchParams.status === null) &&
          selectedRowKeys.length === 0),
    },
  ]

  const rowSelection: TableRowSelection<AccountVestingType> = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys)
    },
    getCheckboxProps: (record) => ({
      disabled: isDisabledStatus(record.status),
    }),
  }

  const columns: ColumnsType<AccountVestingType> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Hash Email',
      dataIndex: 'hashEmail',
      key: 'hashEmail',
      render: (text) => (
        <span style={{ color: '#000' }}>
          {text?.slice(0, 8)}...{text?.slice(-8)}
        </span>
      ),
    },
    {
      title: type ? ALLOCATION_TITLE[type] : 'Allocation',
      dataIndex: 'allocation',
      key: 'allocation',
      align: 'right',
      render: (value) => value?.toLocaleString() || '0',
    },
    {
      title: 'Whitelist Allocation',
      dataIndex: 'allocationWhitelist',
      key: 'allocationWhitelist',
      align: 'right',
      render: (value) => value?.toLocaleString() || '0',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: number) => {
        const statusInfo = ACCOUNT_VESTING_STATUS_MAP[status] || {
          label: 'Unknown',
          color: 'default',
        }
        return (
          <Tag color={statusInfo.color} style={{ fontSize: 14 }}>
            {statusInfo.label}
          </Tag>
        )
      },
    },
  ]

  return (
    <div className="main-section vesting-data">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title" style={{ marginBottom: 20 }}>
        {title || 'Vesting Data'}
      </div>

      <div className="vesting-data-toolbar">
        <div className="vesting-data-search">
          <Input
            placeholder="Search by email or hash email"
            value={searchParams.keyword || ''}
            onChange={(e) =>
              onSearch({
                keyword: e.target.value,
              } as AccountVestingSearchParams)
            }
            style={{ width: 280 }}
            allowClear
          />
          <Select
            placeholder="Filter by status"
            options={ACCOUNT_VESTING_STATUS_OPTIONS}
            value={searchParams.status}
            onChange={(value) => onSearch({ status: value } as AccountVestingSearchParams)}
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={() => onSearch()}>
            Search
          </Button>
        </div>

        <Space className="vesting-data-actions">
          <ProtectedButton
            icon={<SyncOutlined />}
            onClick={handleSyncData}
            feature={FEATURE.ACCOUNT_VESTING}
            requireCRUD
          >
            Sync Data
          </ProtectedButton>
          <Dropdown menu={{ items: approveMenuItems }} trigger={['click']}>
            <ProtectedButton type="primary" feature={FEATURE.ACCOUNT_VESTING} requireCRUD>
              Approve <DownOutlined />
            </ProtectedButton>
          </Dropdown>
        </Space>
      </div>

      <Card style={{ textAlign: 'left' }}>
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 8 }}>Selected {selectedRowKeys.length} items</span>
            <Button size="small" onClick={() => setSelectedRowKeys([])}>
              Clear
            </Button>
          </div>
        )}
        <Table
          className="vesting-data-table"
          columns={columns}
          dataSource={data}
          rowKey={(record) => record._id}
          loading={loading}
          pagination={initPagination(total)}
          onChange={onChangePagination}
          scroll={{ x: 800 }}
          bordered
          rowSelection={rowSelection}
        />
      </Card>
    </div>
  )
}

export default AccountVestingContent
